import { DefaultApi, Configuration, RequiredError, ResponseError } from "src/api/generated/frontend_common_v1";
import { ERROR_STORE } from "src/context/error-context";
import Console from "src/utils/console";

/** Hyphen separated uuid */
export type UUID = string;

const configuration = new Configuration({
    basePath: window.location.origin,
});

const defaultApi = new DefaultApi(configuration);

export const ApiCommon = {
    accounts: {
        getMe: () => handleError(defaultApi.getMyAccount()),
    },
    version: {
        get: () => handleError(defaultApi.getCurrentVersion()),
    },
};

/**
 * Wraps a promise returned by the generated SDK which handles its errors and returns a {@link Result}
 *
 * @param promise The promise to wrap. This should be a promise defined in the generated part of the API
 *
 * @returns a new promise with a result that wraps errors from the API
 */
export async function handleError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (e) {
        let msg;
        if (e instanceof ResponseError) {
            if (e.response.status === 401) {
                // HTTP/2 has no reason phrase, so e.response.statusText is "" there. Report a
                // stable sentinel (keyed off the 401 status code) so ErrorContext's
                // `error === "Unauthorized"` check works over HTTP/2, not just HTTP/1.1.
                msg = "Unauthorized";
            } else {
                try {
                    const err = await e.response.json();
                    msg = `${e.response.statusText}. TraceId: ${err.trace_id}`;
                } catch {
                    Console.error("Got invalid json", e.response.body);
                    msg = `${e.response.statusText}. The server's response was invalid json.`;
                }
            }
        } else if (e instanceof RequiredError) {
            Console.error(e);
            msg = "The server's response didn't match the spec";
        } else {
            Console.error("Unknown error occurred:", e);
            msg = "Unknown error occurred";
        }
        ERROR_STORE.report(msg);
        throw msg;
    }
}
